import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { 
  Users, 
  UserPlus, 
  Key, 
  Trash2, 
  ShieldCheck,
  Loader2,
  Search,
  Grid3X3,
  TrendingUp,
  Calendar,
  BarChart3,
  Activity,
  Clock,
  Calculator,
  Timer,
  Wrench
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mini line chart component for user growth
const MiniChart = ({ data, height = 60 }) => {
  if (!data || data.length === 0) return null;
  
  const maxVal = Math.max(...data.map(d => d.cumulative), 1);
  const width = 100;
  
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (d.cumulative / maxVal) * height;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        points={points}
        className="text-cyan-400"
      />
      <polyline
        fill="url(#gradient)"
        stroke="none"
        points={`0,${height} ${points} ${width},${height}`}
      />
      <defs>
        <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgb(34, 211, 238)" stopOpacity="0.3" />
          <stop offset="100%" stopColor="rgb(34, 211, 238)" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};

// Bar chart for tool usage
const ToolUsageChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No tool usage data yet</p>
        <p className="text-xs mt-1">Usage will be tracked as users interact with tools</p>
      </div>
    );
  }
  
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const categoryColors = {
    Planning: "bg-cyan-500",
    Execution: "bg-yellow-500",
    Solver: "bg-purple-500",
    Other: "bg-gray-500"
  };
  
  return (
    <div className="space-y-3">
      {data.slice(0, 8).map((item, i) => (
        <div key={item.tool} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="capitalize">{item.tool.replace(/-/g, ' ')}</span>
            <span className="text-muted-foreground">{item.count} uses</span>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div 
              className={`h-full ${categoryColors[item.category]} transition-all duration-500`}
              style={{ width: `${(item.count / maxCount) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export const AdminConsole = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialog states
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", is_admin: false });
  const [newPassword, setNewPassword] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        toast.error("Failed to fetch users");
      }
    } catch (error) {
      toast.error("Error fetching users");
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify(newUser)
      });
      
      if (response.ok) {
        toast.success("User created successfully");
        setAddUserOpen(false);
        setNewUser({ name: "", email: "", password: "", is_admin: false });
        fetchUsers();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create user");
      }
    } catch (error) {
      toast.error("Error creating user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    setActionLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify({ new_password: newPassword })
      });
      
      if (response.ok) {
        toast.success("Password reset successfully");
        setResetPasswordOpen(false);
        setNewPassword("");
        setSelectedUser(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reset password");
      }
    } catch (error) {
      toast.error("Error resetting password");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success("User deleted successfully");
        setDeleteUserOpen(false);
        setSelectedUser(null);
        fetchUsers();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete user");
      }
    } catch (error) {
      toast.error("Error deleting user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleAdmin = async (user) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify({ is_admin: !user.is_admin })
      });
      
      if (response.ok) {
        toast.success(`Admin status ${!user.is_admin ? 'granted' : 'revoked'}`);
        fetchUsers();
        fetchStats();
      } else {
        toast.error("Failed to update admin status");
      }
    } catch (error) {
      toast.error("Error updating admin status");
    }
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateShort = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Layout 
      title="Admin Console" 
      subtitle="Manage users and view analytics"
    >
      <div className="max-w-6xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="dashboard-tab">
              <BarChart3 className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2" data-testid="users-tab">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {statsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
              </div>
            ) : stats ? (
              <>
                {/* Quick Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="tactical-card">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-cyan-500/20">
                          <Users className="h-6 w-6 text-cyan-400" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold">{stats.total_users}</p>
                          <p className="text-sm text-muted-foreground">Total Users</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="tactical-card">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-500/20">
                          <TrendingUp className="h-6 w-6 text-green-400" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-green-400">+{stats.new_users_week}</p>
                          <p className="text-sm text-muted-foreground">This Week</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="tactical-card">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-yellow-500/20">
                          <ShieldCheck className="h-6 w-6 text-yellow-400" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold">{stats.total_admins}</p>
                          <p className="text-sm text-muted-foreground">Admins</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="tactical-card">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-500/20">
                          <Grid3X3 className="h-6 w-6 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold">{stats.total_floor_plans}</p>
                          <p className="text-sm text-muted-foreground">Floor Plans</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* User Growth Chart */}
                  <Card className="tactical-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <TrendingUp className="h-5 w-5 text-cyan-400" />
                        User Growth (30 Days)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-40">
                        <MiniChart data={stats.user_growth} height={140} />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground mt-2">
                        <span>{formatDateShort(stats.user_growth[0]?.date)}</span>
                        <span>{formatDateShort(stats.user_growth[stats.user_growth.length - 1]?.date)}</span>
                      </div>
                      <div className="mt-4 pt-4 border-t border-border/50 grid grid-cols-2 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold text-cyan-400">+{stats.new_users_today}</p>
                          <p className="text-xs text-muted-foreground">Today</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-green-400">+{stats.new_users_week}</p>
                          <p className="text-xs text-muted-foreground">This Week</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Tool Usage Chart */}
                  <Card className="tactical-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Activity className="h-5 w-5 text-purple-400" />
                        Most Used Tools
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ToolUsageChart data={stats.tool_usage} />
                      {stats.tool_usage.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border/50">
                          <div className="flex items-center gap-4 text-xs">
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-cyan-500" />
                              <span className="text-muted-foreground">Planning</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-yellow-500" />
                              <span className="text-muted-foreground">Execution</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-purple-500" />
                              <span className="text-muted-foreground">Solver</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Users */}
                <Card className="tactical-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Clock className="h-5 w-5 text-yellow-400" />
                      Recent Registrations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats.recent_users.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No recent users</p>
                    ) : (
                      <div className="space-y-3">
                        {stats.recent_users.map((user) => (
                          <div key={user.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium flex items-center gap-2">
                                  {user.name}
                                  {user.is_admin && <ShieldCheck className="h-4 w-4 text-yellow-400" />}
                                </p>
                                <p className="text-sm text-muted-foreground">{user.email}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">{formatDate(user.created_at)}</p>
                              <p className="text-xs text-cyan-400">{user.floor_plan_count} plans</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                Failed to load dashboard stats
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            {/* Header Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="tactical-input pl-10"
                  data-testid="search-users"
                />
              </div>
              <Button 
                onClick={() => setAddUserOpen(true)}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="add-user-button"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="tactical-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-cyan-400" />
                    <div>
                      <p className="text-2xl font-bold">{users.length}</p>
                      <p className="text-sm text-muted-foreground">Total Users</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="tactical-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="h-8 w-8 text-yellow-400" />
                    <div>
                      <p className="text-2xl font-bold">{users.filter(u => u.is_admin).length}</p>
                      <p className="text-sm text-muted-foreground">Admins</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="tactical-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Grid3X3 className="h-8 w-8 text-purple-400" />
                    <div>
                      <p className="text-2xl font-bold">{users.reduce((sum, u) => sum + u.floor_plan_count, 0)}</p>
                      <p className="text-sm text-muted-foreground">Floor Plans</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="tactical-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-green-400" />
                    <div>
                      <p className="text-2xl font-bold">{users.filter(u => !u.is_admin).length}</p>
                      <p className="text-sm text-muted-foreground">Regular Users</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Users Table */}
            <Card className="tactical-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-cyan-400" />
                  User Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">User</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Email</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Admin</th>
                          <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Plans</th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Created</th>
                          <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((user) => (
                          <tr key={user.id} className="border-b border-border/50 hover:bg-secondary/30">
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                {user.is_admin && <ShieldCheck className="h-4 w-4 text-yellow-400" />}
                                <span className="font-medium">{user.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-muted-foreground">{user.email}</td>
                            <td className="py-3 px-2 text-center">
                              <Switch
                                checked={user.is_admin}
                                onCheckedChange={() => handleToggleAdmin(user)}
                                data-testid={`admin-toggle-${user.id}`}
                              />
                            </td>
                            <td className="py-3 px-2 text-center">
                              <span className="font-mono text-cyan-400">{user.floor_plan_count}</span>
                            </td>
                            <td className="py-3 px-2 text-muted-foreground text-sm">
                              {formatDate(user.created_at)}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setResetPasswordOpen(true);
                                  }}
                                  title="Reset Password"
                                  data-testid={`reset-password-${user.id}`}
                                >
                                  <Key className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setDeleteUserOpen(true);
                                  }}
                                  className="text-destructive hover:text-destructive"
                                  title="Delete User"
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {filteredUsers.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No users found
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user account with email and password
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="User's name"
                required
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@example.com"
                required
                data-testid="new-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Minimum 6 characters"
                required
                minLength={6}
                data-testid="new-user-password"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={newUser.is_admin}
                onCheckedChange={(checked) => setNewUser({ ...newUser, is_admin: checked })}
                data-testid="new-user-admin"
              />
              <Label>Make Admin</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddUserOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading} data-testid="create-user-submit">
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setResetPasswordOpen(false);
              setNewPassword("");
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword} 
              disabled={actionLoading || newPassword.length < 6}
              data-testid="reset-password-submit"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteUserOpen} onOpenChange={setDeleteUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedUser?.name}? This will also delete all their floor plans. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUserOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser}
              disabled={actionLoading}
              data-testid="delete-user-confirm"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminConsole;
